import { z } from "zod";

import { VOICE_GROUP_IDS } from "@/shared/group-configs/groups";

export const EVAL_RUN_SPEC_VERSION = 2;
export const MAX_EVAL_ATTEMPTS_PER_RUN = 200;
export const MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN = 1_000;
export const MAX_BACKEND_ELIGIBILITY_CASE_ATTEMPTS_PER_RUN = 500;
export const EVAL_RUNNER_TASK_TIMEOUT_SECONDS = 10_800;
export const EVAL_SUITES = ["scenarios", "integration", "backend"] as const;
export enum EvalAgentRuntime {
  Cascaded = "cascaded",
  GptLive = "gpt-live",
  SlowThinker = "slow-thinker",
}

export const EVAL_AGENT_RUNTIMES = Object.values(EvalAgentRuntime);

export enum EvalExecutionMode {
  Audio = "audio",
  Text = "text",
}

const safeIdentifierSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/, "contains unsupported characters");
const modelIdentifierSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/, "contains unsupported characters");
const organizationSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*$/, "must be a lowercase organization slug");
const runNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[^\u0000-\u001f\u007f]+$/, "must not contain control characters");

export const evalAgentVariantSchema = z.enum(["cascaded", "integration", "slow-thinker"]);
export const evalAgentRuntimeSchema = z.enum(EvalAgentRuntime);
export const evalExecutionModeSchema = z.enum(EvalExecutionMode);
export const evalSpeechLanguageSchema = z.enum(["en", "es"]);
export const evalSttLanguageSchema = z.enum(["en", "es", "multi"]);
export const evalScenarioIdSchema = safeIdentifierSchema;
export const evalSuiteSchema = z.enum(EVAL_SUITES);

export type EvalAgentVariantName = z.infer<typeof evalAgentVariantSchema>;
export type EvalSpeechLanguage = z.infer<typeof evalSpeechLanguageSchema>;
export type EvalSttLanguage = z.infer<typeof evalSttLanguageSchema>;
export type EvalSuite = z.infer<typeof evalSuiteSchema>;

export const evalScenarioSelectionSchema = z
  .object({
    type: z.literal("scenarios"),
    scenarioIds: z.array(evalScenarioIdSchema).min(1).max(MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN),
  })
  .strict();

export const evalGroupSelectionSchema = z
  .object({
    type: z.literal("group"),
    groupId: z.enum(VOICE_GROUP_IDS),
  })
  .strict();

export const evalPackSelectionSchema = z
  .object({
    type: z.literal("pack"),
    packId: evalScenarioIdSchema,
  })
  .strict();

export const evalTextExecutionSchema = z
  .object({
    mode: z.literal(EvalExecutionMode.Text),
    scenarioConcurrency: z.number().int().min(1).max(20).default(1),
  })
  .strict();

export const evalAudioExecutionSchema = z
  .object({
    mode: z.literal(EvalExecutionMode.Audio),
    scenarioConcurrency: z.number().int().min(1).max(10).default(1),
    captureAudio: z.boolean().default(true),
    callerTts: z
      .object({
        model: modelIdentifierSchema,
        voice: safeIdentifierSchema.optional(),
        language: evalSpeechLanguageSchema.optional(),
      })
      .strict(),
    callerPauseProfile: z.enum(["none", "hesitant"]).optional(),
    agentSttModel: modelIdentifierSchema,
    agentSttLanguage: evalSttLanguageSchema.optional(),
    agentTtsModel: modelIdentifierSchema,
    agentTtsLanguage: evalSpeechLanguageSchema.optional(),
    interruptionsEnabled: z.boolean().default(true),
  })
  .strict();

export const evalExecutionSchema = z.discriminatedUnion("mode", [evalTextExecutionSchema, evalAudioExecutionSchema]);

export const evalPullRequestSchema = z
  .object({
    owner: safeIdentifierSchema,
    repository: safeIdentifierSchema,
    number: z.number().int().positive(),
    headGitSha: z.string().regex(/^[a-f0-9]{40}$/i, "must be a full git SHA"),
  })
  .strict();

export const evalRunSpecSchema = z
  .object({
    version: z.literal(EVAL_RUN_SPEC_VERSION),
    runId: z.string().uuid(),
    // Optional for version-2 compatibility. New dispatchers persist both fields
    // so the worker can maintain the compact history index without rereading catalogs.
    createdAt: z.string().datetime().optional(),
    scenarioCount: z.number().int().positive().max(MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN).optional(),
    name: runNameSchema,
    requestedBy: z
      .object({
        userId: safeIdentifierSchema,
        email: z.string().email().optional(),
      })
      .strict(),
    source: z
      .object({
        gitSha: z.string().regex(/^[a-f0-9]{40}$/i, "must be a full git SHA"),
        imageDigest: z
          .string()
          .regex(/^sha256:[a-f0-9]{64}$/i)
          .optional(),
        pullRequest: evalPullRequestSchema.optional(),
      })
      .strict(),
    suite: evalSuiteSchema,
    organization: organizationSchema.optional(),
    selection: z.discriminatedUnion("type", [
      evalScenarioSelectionSchema,
      evalGroupSelectionSchema,
      evalPackSelectionSchema,
    ]),
    attempts: z.number().int().min(1).max(100),
    agentVariant: evalAgentVariantSchema,
    // Optional for version-2 spec compatibility; new dispatchers always persist it explicitly.
    agentRuntime: evalAgentRuntimeSchema.optional(),
    models: z
      .object({
        agent: modelIdentifierSchema,
        provider: safeIdentifierSchema.optional(),
        judge: modelIdentifierSchema,
      })
      .strict(),
    quality: z.boolean().default(false),
    execution: evalExecutionSchema,
  })
  .strict()
  .superRefine((spec, ctx) => {
    const agentRuntime = spec.agentRuntime ?? getDefaultEvalAgentRuntime(spec.agentVariant);

    if (
      spec.source.pullRequest &&
      spec.source.pullRequest.headGitSha.toLowerCase() !== spec.source.gitSha.toLowerCase()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "pull request head must match the eval source",
        path: ["source", "pullRequest", "headGitSha"],
      });
    }

    if (
      spec.selection.type === "scenarios" &&
      new Set(spec.selection.scenarioIds).size !== spec.selection.scenarioIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "scenarioIds must not contain duplicates",
        path: ["selection", "scenarioIds"],
      });
    }

    if (spec.suite === "integration" && spec.attempts !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs must use exactly one attempt",
        path: ["attempts"],
      });
    }

    if (
      spec.suite === "integration" &&
      spec.execution.mode === EvalExecutionMode.Audio &&
      spec.execution.scenarioConcurrency !== 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "audio integration runs require scenario concurrency 1",
        path: ["execution", "scenarioConcurrency"],
      });
    }

    if (spec.suite === "integration" && spec.agentVariant !== "integration") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs must use the integration agent variant",
        path: ["agentVariant"],
      });
    }

    if (spec.suite === "scenarios" && spec.agentVariant === "integration") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the integration agent variant requires the integration suite",
        path: ["suite"],
      });
    }

    if (spec.suite === "backend" && spec.agentVariant !== "cascaded") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs use the cascaded variant as their non-agent execution marker",
        path: ["agentVariant"],
      });
    }

    if (spec.suite === "backend" && spec.execution.mode !== EvalExecutionMode.Text) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs support text execution only",
        path: ["execution", "mode"],
      });
    }

    if (spec.suite === "backend" && spec.selection.type === "group") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs require a scenario or pack selection",
        path: ["selection"],
      });
    }

    if (spec.suite !== "backend" && spec.selection.type === "pack") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "pack selections require the backend suite",
        path: ["selection"],
      });
    }

    if (spec.suite === "backend" && spec.quality) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs do not support conversation quality judging",
        path: ["quality"],
      });
    }

    const runtimeMatchesVariant =
      spec.agentVariant === "integration" ||
      agentRuntime === getDefaultEvalAgentRuntime(spec.agentVariant) ||
      (spec.agentVariant === "slow-thinker" && agentRuntime === "gpt-live");

    if (!runtimeMatchesVariant) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `the ${spec.agentVariant} agent variant does not support the ${agentRuntime} runtime`,
        path: ["agentRuntime"],
      });
    }

    if (agentRuntime === "gpt-live" && spec.execution.mode !== "audio") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the gpt-live runtime supports audio execution only",
        path: ["execution", "mode"],
      });
    }

    if (agentRuntime === "gpt-live" && spec.models.provider) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the gpt-live runtime uses OpenAI directly and does not accept a provider",
        path: ["models", "provider"],
      });
    }

    if (agentRuntime === "gpt-live" && spec.models.agent.includes("/")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the gpt-live runtime requires a direct OpenAI model identifier",
        path: ["models", "agent"],
      });
    }

    if (spec.selection.type !== "scenarios") return;

    try {
      validateEvalAttemptBudget(spec.selection.scenarioIds, spec.attempts, spec.suite);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: error instanceof Error ? error.message : String(error),
        path: ["attempts"],
      });
    }
  });

export type EvalRunSpec = z.infer<typeof evalRunSpecSchema>;
export type EvalRunSelection = EvalRunSpec["selection"];

/** Backward-compatible runtime for specs written before runtime was separated from authorization variant. */
export function getDefaultEvalAgentRuntime(agentVariant: EvalAgentVariantName): EvalAgentRuntime {
  switch (agentVariant) {
    case "slow-thinker":
      return EvalAgentRuntime.SlowThinker;
    case "cascaded":
    case "integration":
      return EvalAgentRuntime.Cascaded;
  }
}

/** Validates untrusted run input before it reaches the eval process or environment. */
export function parseEvalRunSpec(input: unknown): EvalRunSpec {
  return evalRunSpecSchema.parse(input);
}

/** Enforces the multiplicative budget after a catalog-based group selection is resolved. */
export function validateEvalAttemptBudget(
  scenarioIds: readonly string[],
  attempts: number,
  suite: EvalSuite = "scenarios"
): void {
  const maxAttempts = suite === "backend" ? MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN : MAX_EVAL_ATTEMPTS_PER_RUN;

  if (suite === "backend") {
    const eligibilityCaseCount = scenarioIds.filter((id) => id.startsWith("backend/check-eligibility/")).length;

    if (eligibilityCaseCount * attempts > MAX_BACKEND_ELIGIBILITY_CASE_ATTEMPTS_PER_RUN) {
      throw new Error(
        `selected eligibility cases * attempts must not exceed ${MAX_BACKEND_ELIGIBILITY_CASE_ATTEMPTS_PER_RUN} per run`
      );
    }
  }

  if (scenarioIds.length * attempts > maxAttempts) {
    throw new Error(`selected scenarios * attempts must not exceed ${maxAttempts} total attempts per run`);
  }
}
